<script setup>
import { Form, FormField } from "@primevue/forms";
import { zodResolver } from "@primevue/forms/resolvers/zod";
import { z } from "zod";

const emit = defineEmits(["onSave", "onClose"]);

const { showTitle, isOnboard } = defineProps({
  showTitle: {
    type: Boolean,
    required: false,
    default: true,
  },
  isOnboard: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const toast = useToast();
const hasError = ref(false);
const preferredContact = ref(null);
// const phoneNumberCode = ref(null);
// const viberOrTelegramCode = ref(null);
const { data: phoneCodes } = useFetch("/api/phone-codes");

const { data: sessionData } = useAuth();

const authUser = computed(() => {
  return sessionData.value?.data;
});

const onFormSubmit = async ({ valid, values, states }) => {
  if (valid) {
    const formData = new FormData();

    if (profileImg.value) {
      formData.append("profile", profileImg.value);
    }

    formData.append("name", states.name.value);
    if (states.phone_number.value) {
      formData.append("phone_number", states.phone_number.value);
    }
    if (preferredContact.value === "viber") {
      formData.append("viber_number", states.viber_number.value);
    } else if (preferredContact.value === "telegram") {
      formData.append("telegram_number", states.telegram_number.value);
    }

    await useApiFetch("profile/update/info", {
      method: "POST",
      body: formData,
      onResponse({ response }) {
        if ([200, 201].includes(response.status)) {
          emit("onSave");
          toast.add({
            severity: "success",
            summary: "You personal info has been saved.",
            life: 3000,
            closable: false,
          });
        }
      },
    });
  }
};

const resolver = zodResolver(
  z.object({
    name: z
      .string({
        required_error: "Please enter your name.",
      })
      .min(6, { message: "Your name must be at least 6 characters long." }),
    phone_number: z
      .string()
      .optional()
      .refine((val) => !val || val.length >= 6, {
        message: "Phone number must be at least 6 digits.",
      }),
    preferred_contact: z.enum(["viber", "telegram"], {
      required_error: "Please select a preferred contact number.",
    }),
    viber_number: z
      .string({
        required_error: "Please enter your viber number.",
      })
      .min(6, { message: "Phone number must be at least 6 digits." }),
    telegram_number: z
      .string({
        required_error: "Please enter your telegram number.",
      })
      .min(6, { message: "Phone number must be at least 6 digits." }),
  }),
);

const initialValues = ref({
  name: "",
  phone_number: "",
  viber_number: "",
  telegram_number: "",
});

const profileImg = ref();
const onProfileSelect = (event) => {
  profileImg.value = event.files?.at(0);
  hasError.value = false;
};

const handleChooseFile = (chooseCallback, clearCallback) => {
  clearCallback();
  chooseCallback();
};

const isLoading = ref(true);

const originalProfile = computed(() => {
  return sessionData.value?.data?.profile?.media?.at(0)?.original_url;
});

const confirmDelete = (event) => {
  confirm.require({
    header: "Confirm",
    message: "Do you want to delete this profile image?",
    rejectProps: {
      label: "Cancel",
      severity: "secondary",
      outlined: true,
    },
    acceptProps: {
      label: "Delete",
      severity: "danger",
    },
    accept: () => {
      onDeleteProfile();
    },
    reject: () => {},
  });
};

const onDeleteProfile = async () => {
  const profileMedia = sessionData.value?.data?.profile?.media?.at(0);

  if (!profileMedia?.id) return;
  await useApiFetch(`media/${profileMedia?.id}`, {
    method: "DELETE",
    async onResponse() {
      originalProfile.value = null;
      await getSession();
    },
  });
};

onMounted(() => {
  const profile = sessionData.value?.data?.profile;
  if (profile) {
    initialValues.value = {
      name: sessionData.value?.data?.name,
      phone_number: profile.phone_number,
      viber_number: profile.viber_number,
      telegram_number: profile.telegram_number,
    };
  } else {
    initialValues.value = {
      ...initialValues,
      name: sessionData.value?.data?.name,
    };
  }

  isLoading.value = false;
});
</script>

<template>
  <div class="w-full">
    <h1 v-if="isOnboard" class="text-[#1C477B] text-[32px] font-bold mb-[10px]">
      Welcome! Let’s Get to Know You
    </h1>
    <p v-if="isOnboard" class="mb-[40px] text-muted">
      Please share your personal details to help us start your onboarding
      journey smoothly
    </p>

    <i v-if="isLoading" class="pi pi-spin pi-spinner"></i>

    <Form
      class="py-2"
      v-else
      :initialValues
      :resolver="resolver"
      @submit="onFormSubmit"
    >
      <div class="space-y-6 mb-10">
        <div>
          <div class="flex items-center gap-6">
            <div class="relative">
              <Avatar
                v-if="profileImg?.objectURL || originalProfile"
                :image="profileImg?.objectURL || originalProfile"
                size="xlarge"
                :pt="{
                  root: 'size-20 rounded-lg',
                  image: 'size-20 rounded-lg object-cover',
                }"
              />
              <Avatar v-else :label="authUser?.name?.at(0)" size="xlarge" />
              <Button
                v-if="originalProfile"
                icon="pi pi-times-circle"
                rounded
                size="small"
                severity="danger"
                class="!p-0 !-top-2 !absolute !-right-2 !size-5"
                @click="confirmDelete"
              />
            </div>
            <FileUpload
              customUpload
              @select="onProfileSelect"
              :maxFileSize="2097152"
              accept="image/jpeg, image/png, image/jpg"
              :pt="{
                header: '!border-0',
                content: '!border-0',
              }"
              invalidFileSizeMessage="File size should be smaller than 2 MB"
            >
              <template
                #header="{ chooseCallback, clearCallback }"
                class="!border-0 !p-0"
              >
                <Button
                  label="Upload"
                  size="small"
                  icon="pi pi-upload"
                  outlined
                  rounded
                  @click="handleChooseFile(chooseCallback, clearCallback)"
                />
              </template>
              <template
                #content="{
                  files,
                  uploadedFiles,
                  removeFileCallback,
                  messages,
                }"
              >
                <div v-if="files.length > 0">
                  <Message
                    v-for="message of messages"
                    :key="message"
                    variant="simple"
                    size="small"
                    severity="error"
                  >
                    {{ message }}
                  </Message>
                </div>
              </template>
            </FileUpload>
          </div>
          <Message
            v-if="hasError"
            severity="error"
            size="small"
            variant="simple"
            :pt="{
              content: 'px-1',
            }"
            >Please upload profile photo.</Message
          >
        </div>
        <FormField
          v-slot="$field"
          :initialValue="initialValues.name"
          name="name"
          class="space-y-1"
        >
          <Label isRequired>Name</Label>
          <InputText
            name="name"
            placeholder="Your name"
            class="placeholder:text-xs lg:placeholder:text-sm text-[#4B5563] text-xs lg:text-sm"
            fluid
          />
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
            :pt="{
              content: 'px-1',
            }"
            >{{ $field?.error.message }}</Message
          >
        </FormField>
        <FormField
          v-slot="$field"
          :initialValue="initialValues.phone_number"
          name="phone_number"
          class="space-y-1"
        >
          <Label>Phone number</Label>
          <div class="flex gap-2">
            <!-- <Select
              v-model="phoneNumberCode"
              :options="phoneCodes"
              filter
              :filterFields="['name']"
              class="!w-[120px] text-xs lg:text-sm"
            >
              <template #option="{ option }">
                <span class="text-[#4B5563] text-xs lg:text-sm"
                  >{{ option.flag }} {{ option.dial_code }}</span
                >
              </template>
              <template #value="{ value }">
                <span
                  v-if="value?.flag"
                  class="text-[#4B5563] text-xs lg:text-sm"
                >
                  {{ value.flag }} {{ value.dial_code }}
                </span>
                <span v-else class="text-gray-400 text-xs lg:text-sm"
                  >Code</span
                >
              </template>
            </Select> -->

            <InputText
              name="phone_number"
              placeholder="+95 123 456 789"
              type="number"
              class="flex-1 placeholder:text-xs lg:placeholder:text-sm text-[#4B5563] text-xs lg:text-sm"
              fluid
            />
          </div>

          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
            :pt="{ content: 'px-1' }"
          >
            {{ $field?.error.message }}
          </Message>
        </FormField>

        <FormField
          v-slot="$field"
          :initialValue="preferredContact"
          name="preferred_contact"
          class="space-y-1"
        >
          <Label isRequired>Preferred Contact Number</Label>
          <Select
            v-model="preferredContact"
            :options="[
              { label: 'Viber', value: 'viber' },
              { label: 'Telegram', value: 'telegram' },
            ]"
            optionLabel="label"
            optionValue="value"
            placeholder="Select a method"
            class="placeholder:text-xs py-[2px] lg:placeholder:text-sm text-[#4B5563] text-xs lg:text-sm"
          />
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
            :pt="{
              content: 'px-1',
            }"
            >{{ $field?.error.message }}</Message
          >
        </FormField>

        <FormField
          v-if="preferredContact === 'viber'"
          v-slot="$field"
          :initialValue="initialValues.viber_number"
          name="viber_number"
          class="space-y-1"
        >
          <Label isRequired>Viber number</Label>
          <div class="flex gap-2">
            <!-- <Select
              v-model="viberOrTelegramCode"
              :options="phoneCodes"
              filter
              :filterFields="['name']"
              class="!w-[120px] text-xs lg:text-sm"
            >
              <template #option="{ option }">
                <span class="text-[#4B5563] text-xs lg:text-sm"
                  >{{ option.flag }} {{ option.dial_code }}</span
                >
              </template>
              <template #value="{ value }">
                <span
                  v-if="value?.flag"
                  class="text-[#4B5563] text-xs lg:text-sm"
                >
                  {{ value.flag }} {{ value.dial_code }}
                </span>
                <span v-else class="text-gray-400 text-xs lg:text-sm"
                  >Code</span
                >
              </template>
            </Select> -->

            <InputText
              name="viber_number"
              placeholder="+95 123 456 789"
              type="number"
              class="flex-1 placeholder:text-xs lg:placeholder:text-sm text-[#4B5563] text-xs lg:text-sm"
              fluid
            />
          </div>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
            :pt="{
              content: 'px-1',
            }"
            >{{ $field?.error.message }}</Message
          >
        </FormField>
        <FormField
          v-slot="$field"
          v-if="preferredContact === 'telegram'"
          :initialValue="initialValues.telegram_number"
          name="telegram_number"
          class="space-y-1"
        >
          <Label isRequired>Telegram number</Label>
          <div class="flex gap-2">
            <!-- <Select
              v-model="viberOrTelegramCode"
              :options="phoneCodes"
              filter
              :filterFields="['name']"
              class="!w-[120px] text-xs lg:text-sm"
            >
              <template #option="{ option }">
                <span class="text-[#4B5563] text-xs lg:text-sm"
                  >{{ option.flag }} {{ option.dial_code }}</span
                >
              </template>
              <template #value="{ value }">
                <span
                  v-if="value?.flag"
                  class="text-[#4B5563] text-xs lg:text-sm"
                >
                  {{ value.flag }} {{ value.dial_code }}
                </span>
                <span v-else class="text-gray-400 text-xs lg:text-sm"
                  >Code</span
                >
              </template>
            </Select> -->

            <InputText
              name="telegram_number"
              placeholder="+95 123 456 789"
              type="number"
              class="flex-1 placeholder:text-xs lg:placeholder:text-sm text-[#4B5563] text-xs lg:text-sm"
              fluid
            />
          </div>
          <Message
            v-if="$field?.invalid"
            severity="error"
            size="small"
            variant="simple"
            :pt="{
              content: 'px-1',
            }"
            >{{ $field?.error.message }}</Message
          >
        </FormField>
      </div>

      <div class="flex items-center gap-3 justify-end">
        <Button
          v-if="isOnboard"
          type="button"
          label="Skip"
          outlined
          class="w-[132px]"
          @click="emit('updateStep', 2)"
        />
        <Button
          v-if="!isOnboard"
          type="button"
          label="Cancel"
          outlined
          @click="emit('onClose')"
          class="w-[145px]"
        ></Button>
        <Button
          type="submit"
          :label="isOnboard ? 'Continue' : 'Save'"
          class="w-[145px]"
        />
      </div>
    </Form>
  </div>
</template>
